import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Video } from '@/types/content';
import { formatPlays, getCategory } from '@/lib/videos';

interface HeroCarouselProps {
  videos: Video[];
}

export default function HeroCarousel({ videos }: HeroCarouselProps) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (videos.length <= 1) return;
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % videos.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [videos.length]);

  if (videos.length === 0) return null;
  const current = videos[active];
  const category = getCategory(current.category);

  return (
    <section className="relative h-[480px] w-full overflow-hidden md:h-[620px]">
      <img
        key={current.id}
        src={current.hero || current.cover}
        alt={current.title}
        title={`${current.title} - 男同视频精选`}
        className="absolute inset-0 h-full w-full animate-fade-in object-cover object-top"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/65 to-primary-950/25" />
      <div className="absolute inset-0 bg-gradient-to-t from-primary-950/70 via-transparent to-primary-950/30" />

      <div className="relative z-10 mx-auto flex h-full w-full max-w-7xl flex-col justify-center px-4 md:px-8">
        <span className="mb-4 flex w-fit items-center gap-2 rounded-full border border-background-50/25 bg-background-50/10 px-3 py-1 text-xs font-medium text-background-100 backdrop-blur-sm">
          <i className="ri-sparkling-2-line text-accent-300" />
          男同视频 · 男同主题影像平台
        </span>

        <h1 className="max-w-3xl text-2xl font-extrabold leading-tight tracking-tight text-background-50 md:text-4xl lg:text-5xl">
          发现打动你的男同影像故事
        </h1>

        <div key={`content-${current.id}`} className="mt-6 max-w-2xl animate-fade-up">
          <div className="mb-2 flex flex-wrap items-center gap-2 text-xs font-medium text-background-200">
            <span className="rounded bg-accent-500 px-2 py-0.5 text-background-50">
              今日精选
            </span>
            {category && (
              <span className="rounded border border-background-50/20 px-2 py-0.5">
                {category.name}
              </span>
            )}
            <span className="flex items-center gap-1">
              <i className="ri-star-fill text-accent-300" />
              {current.rating.toFixed(1)}
            </span>
            <span>{current.year}</span>
            <span>·</span>
            <span>{current.duration}</span>
          </div>
          <h2 className="text-xl font-bold text-background-50 md:text-3xl">
            <Link to={`/detail/${current.id}`} className="hover:text-accent-300">
              {current.title}
            </Link>
          </h2>
          <p className="mt-3 line-clamp-2 text-sm leading-relaxed text-background-200/90 md:text-base">
            {current.description}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${current.id}`}
              className="flex items-center gap-2 whitespace-nowrap rounded-full bg-accent-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
            >
              <i className="ri-play-fill text-base" />
              立即观看
            </Link>
            <Link
              to={`/category/${current.category}`}
              className="flex items-center gap-2 whitespace-nowrap rounded-full border border-background-50/30 bg-background-50/10 px-6 py-2.5 text-sm font-semibold text-background-50 backdrop-blur-sm transition-colors hover:bg-background-50/20"
            >
              浏览同类内容
              <i className="ri-arrow-right-line" />
            </Link>
            <span className="flex items-center gap-1 text-xs text-background-300">
              <i className="ri-fire-line text-accent-300" />
              {formatPlays(current.plays)}次播放
            </span>
          </div>
        </div>

        <div className="mt-8 flex items-center gap-2">
          {videos.map((video, index) => (
            <button
              key={video.id}
              type="button"
              aria-label={`切换到 ${video.title}`}
              onClick={() => setActive(index)}
              className={`h-1.5 rounded-full transition-all ${
                index === active ? 'w-8 bg-accent-500' : 'w-3 bg-background-50/40 hover:bg-background-50/70'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}